import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'gold' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'
  children?: ReactNode
}

const SIZES: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '7px 13px', fontSize: 13.5 },
  md: { padding: '10px 18px', fontSize: 15 },
  lg: { padding: '13px 24px', fontSize: 16 },
}

const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--brand-primary)',
    color: 'var(--paper-0)',
    border: '1px solid var(--brand-primary)',
    boxShadow: 'var(--shadow-sm)',
  },
  secondary: {
    background: 'var(--surface-card)',
    color: 'var(--ink-1)',
    border: '1px solid var(--border-strong)',
    boxShadow: 'var(--shadow-xs)',
  },
  gold: {
    background: 'var(--brand-accent)',
    color: 'var(--ink-0)',
    border: '1px solid var(--brand-accent)',
    boxShadow: 'var(--shadow-gold)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--ink-1)',
    border: '1px solid transparent',
  },
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  iconLeft,
  iconRight,
  loading,
  type = 'button',
  children,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontFamily: 'var(--font-body)',
        fontWeight: 600,
        borderRadius: 'var(--radius-md)',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.6 : 1,
        width: fullWidth ? '100%' : undefined,
        transition:
          'background var(--dur-fast) var(--ease-standard), transform var(--dur-fast) var(--ease-standard), box-shadow var(--dur-fast) var(--ease-standard)',
        whiteSpace: 'nowrap',
        ...SIZES[size],
        ...VARIANTS[variant],
        ...style,
      }}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  )
}

export default Button
